import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, Mountain, Loader2, ExternalLink, Save } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * iter122 — Admin card to update the running JustGiving donation total.
 *
 * The JustGiving public read API requires an OAuth-approved client. For
 * the MVP, the owner just pastes the figure they see on the JustGiving
 * page into this card every so often. Takes ~5 seconds and avoids a
 * brittle HTML scrape.
 */
export default function CharityDonationsAdminCard() {
  const [current, setCurrent] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/charity/donations").then(({ data }) => {
      if (!alive) return;
      setCurrent(data);
      setDraft(String(Number(data?.total_raised_gbp || 0)));
    }).catch(() => { /* swallow */ })
    .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const value = Number(draft);
    if (Number.isNaN(value) || value < 0) {
      toast.error("Enter a valid amount in £.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/charity/donations", { total_raised_gbp: value });
      setCurrent((c) => ({ ...(c || {}), total_raised_gbp: data.total_raised_gbp, updated_at: data.updated_at }));
      toast.success(`Donation total updated to £${value.toFixed(0)}`);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="border-2 hairline-strong bg-white p-6">
        <div className="flex items-center text-sm text-[#555]">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="border-2 hairline-strong bg-white p-6" data-testid="admin-charity-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-[#16a34a] text-white flex-shrink-0">
          <Mountain className="w-4 h-4" strokeWidth={2.4} />
        </div>
        <div className="min-w-0">
          <div className="eyebrow text-[#ff4500] mb-0.5 inline-flex items-center gap-1.5">
            <Heart className="w-3 h-3 fill-current" /> Mountain Rescue donations
          </div>
          <h3 className="font-display text-xl tracking-tight leading-tight">Running JustGiving total</h3>
          <p className="text-xs text-[#666] mt-1 leading-snug">
            Paste the figure from your JustGiving page after a donation lands.
            When this is £0, the homepage ribbon hides the amount and just shows the donate CTA.
          </p>
        </div>
      </div>

      <form onSubmit={save} className="flex flex-wrap items-end gap-3 mb-3">
        <div className="flex-1 min-w-[160px]">
          <label htmlFor="charity-total-input" className="block text-[10px] mono uppercase tracking-wider text-[#666] mb-1">
            Total raised (£ GBP)
          </label>
          <input
            id="charity-total-input"
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full px-3 py-2 border-2 border-[#0e0e0e] focus:outline-none focus:ring-2 focus:ring-[#ff4500] font-display text-2xl"
            placeholder="0"
            data-testid="admin-charity-total-input"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0e0e0e] text-white hover:bg-[#ff4500] disabled:opacity-60 transition-colors text-xs mono uppercase tracking-wider"
          data-testid="admin-charity-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? "Saving…" : "Save total"}
        </button>
      </form>

      {current?.updated_at && (
        <div className="text-[11px] mono text-[#666] uppercase tracking-wider mb-3">
          Last updated · {new Date(current.updated_at).toLocaleString("en-GB")}
        </div>
      )}

      <a
        href={current?.justgiving_url || "https://www.justgiving.com/page/camp-compass"}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#16a34a] hover:text-[#0e0e0e]"
      >
        Open JustGiving page <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}
